import {ReactNode} from "react";
import {css} from "@panda/css";
import cn from "classnames";

import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {AddToFavouritesButton} from "../../../favorite/components/AddToFavouritesButton";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../hooks/use_should_show_price_by_offer_config_and_user";
import {PropertyFlatType} from "../../../offer/detail/constants/PropertyFlatType";
import {IOfferSalesOffice} from "../../../offer/types/IOfferSalesOffice";
import {OfferModalActiveTab} from "../../../offer/types/OfferModalActiveTab";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {AmplitudeAuthPlacement} from "../../../tracking/types/AmplitudeAuthPlacement";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {useLeadData} from "../../hooks/use_lead_data";
import {ApplicationFormVariant} from "../../types/form/ApplicationFormVariant";
import {GTMEventApplicationSourceSection} from "../../types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {getApplicationFormBoxCtaText} from "../../utils/lead/get_application_form_box_cta_text";
import {getApplicationLeadButtonText} from "../../utils/lead/get_application_lead_button_text";
import {ApplicationModalButtonWithHiddenPrices, IApplicationModalButtonWithHiddenPricesRenderProps} from "../ApplicationModalButtonWithHiddenPrices";
import {ApplicationTermsAcceptation} from "../ApplicationTermsAcceptation";
import {ApplicationFormBoxDefaultRender} from "./ApplicationFormBoxDefaultRender";
import {ApplicationFormBoxHeading} from "./ApplicationFormBoxHeading";
import {ApplicationFormBoxHiddenPriceVariant} from "./ApplicationFormBoxHiddenPriceVariant";
import {ApplicationFormBoxPhoneNumber} from "./ApplicationFormBoxPhoneNumber";

interface IProps {
    applicationVariant?: ApplicationFormVariant;
    customApplicationButtonText?: string;
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    optionalVendorInfoData?: {
        name: string;
        logo: {
            v_log_120x90: string;
        } | null;
    };
    optionalOfferPhoneData?: IOfferPhoneData;
    offerStatusStatus?: OfferStatusStatus;
    isAutoresponderEnabled: boolean;
    sidebarBg?: "light" | "dark";
    vendorId: number;
    offerId?: number;
    property?: (IPropertyDetail | IPropertyListProperty) & {flat_type?: PropertyFlatType};
    forceClose?: boolean;
    disableVendorInfo?: boolean;
    formButtonText?: string;
    render?: (props: IApplicationModalButtonWithHiddenPricesRenderProps) => ReactNode;
    className?: string;
    footer?: ReactNode;
    officeData?: IOfferSalesOffice;
    modalState?: OfferModalActiveTab | null;
}

interface IOfferPhoneData {
    configuration: {
        phone_clip: string | null;
    };
    is_phone_clip_enabled: boolean;
}

export const ApplicationFormBox = (props: IProps) => {
    const {
        customApplicationButtonText,
        source,
        sourceSection,
        optionalVendorInfoData,
        optionalOfferPhoneData,
        isAutoresponderEnabled,
        sidebarBg,
        vendorId,
        offerId,
        property,
        forceClose,
        className,
        modalState,
        offerStatusStatus
    } = props;

    const propertyId = property?.id;

    const {shouldHidePrices} = useShouldHidePrices();
    const leadData = useLeadData({
        vendorId,
        offerId,
        propertyId
    });

    const isAuthenticated = useIsAuthenticated();
    const isDataFetched = Boolean(leadData);

    const vendor = optionalVendorInfoData ?? leadData?.vendor?.detail;
    const isOfferStatusToAcquire = offerStatusStatus === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE;
    const propertyPrice = leadData?.property?.detail?.price;
    const vendorName = vendor?.name ?? vendorId.toString();
    const offerName = leadData?.offer?.detail?.name ?? (offerId ? offerId.toString() : "");

    const shouldShowPriceByConfig = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: leadData?.offer?.detail?.configuration.price_visibility,
        vendorPriceVisibility: leadData?.vendor?.detail?.configuration.price_visibility,
        vendorId
    });

    const {shouldShowPrice, ctaText} = getApplicationFormBoxCtaText({
        shouldHidePrices,
        isDataFetched,
        propertyId,
        propertyPrice
    });

    let defaultRenderSource;

    switch (modalState) {
        case OfferModalActiveTab.MAP:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_MAP;
            break;
        case OfferModalActiveTab.PLAN:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_FLOOR_PLAN;
            break;
        case OfferModalActiveTab.CONSTRUCTION_GALLERY:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_CONSTRUCTION_GALLERY;
            break;
        case OfferModalActiveTab.GALLERY:
        default:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_GALLERY;
    }

    const amplitudePlacement: AmplitudeAuthPlacement = modalState ? "rp_developer_section_details" : "rp_developer_section_main";

    const gtmDefaultRenderData = {
        clickText: getApplicationLeadButtonText({
            isPriceHidden: !shouldShowPrice,
            isProperty: !!propertyId,
            propertyPrice: propertyPrice
        }),
        source: modalState ? defaultRenderSource : GTMEventApplicationSourceSection.SIDEBAR_LEAD,
        vendorName,
        offerName
    };

    const renderApplicationButton = (renderProps: IApplicationModalButtonWithHiddenPricesRenderProps) => {
        if (!shouldShowPrice) {
            const priceRevealButtonTestId = propertyId
                ? OFFER_TEST_IDS.PROPERTY_VIEW.REVEAL_PRICE_BUTTON
                : OFFER_TEST_IDS.OFFER_VIEW.SIDEBAR_BUTTON_SHOW_PRICES;

            return (
                <ApplicationFormBoxHiddenPriceVariant
                    {...renderProps}
                    buttonText={customApplicationButtonText}
                    priceRevealButtonTestId={priceRevealButtonTestId}
                />
            );
        }

        if (props.render) {
            return props.render(renderProps);
        }

        return (
            <ApplicationFormBoxDefaultRender
                vendorId={vendorId}
                isProperty={!!propertyId}
                price={propertyPrice}
                shouldShowPrice={shouldShowPrice}
                handleClick={renderProps.handleClick}
            />
        );
    };

    return (
        <>
            <div className={cn(cardWrapper, className)}>
                <div className={contactWrapper} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.SIDEBOX}>
                    <ApplicationFormBoxHeading
                        vendorName={vendor?.name}
                        vendorLogo={vendor?.logo && vendor.logo.v_log_120x90}
                        price={propertyPrice}
                        isDataFetched={isDataFetched}
                        shouldShowPrice={shouldShowPriceByConfig}
                        variant={shouldShowPriceByConfig ? "visible" : "blur"}
                        currency={leadData?.property?.detail?.currency}
                        offerType={property?.offer.type}
                        flatType={property?.flat_type}
                        isHolidayLocation={property?.offer.is_holiday_location}
                        isCondoHotel={property?.offer.is_condohotel}
                    />

                    <ApplicationModalButtonWithHiddenPrices
                        source={source}
                        sourceSection={sourceSection}
                        vendorId={vendorId}
                        offerId={offerId || null}
                        propertyId={propertyId || null}
                        forceClose={forceClose}
                        shouldShowPrice={shouldShowPrice}
                        gtmData={{...gtmDefaultRenderData, clickText: ctaText}}
                        amplitudePlacement={amplitudePlacement}
                        render={renderApplicationButton}
                        applicationVariant={props.applicationVariant}
                        currency={property?.offer.currency}
                    />

                    <ApplicationFormBoxPhoneNumber
                        isAutoresponderEnabled={isAutoresponderEnabled}
                        isDataFetched={isDataFetched}
                        offerId={offerId}
                        offer={leadData?.offer?.detail}
                        propertyId={propertyId}
                        optionalOfferPhoneData={optionalOfferPhoneData}
                        useHotlineNumber={isOfferStatusToAcquire}
                    />

                    <ApplicationTermsAcceptation className={css({mt: 2})} sourceForm={{source: "applicationSideBar", isUserLogged: isAuthenticated}} />

                    <Text align="center" color="var(--colors-gray-700)" variant="info_txt_1">
                        Administratorem Twoich danych
                        <br /> osobowych jest Property Group sp. z o.o
                    </Text>
                </div>

                {props.footer}
            </div>

            {propertyId && offerId && (
                <div className={addToFavouritesButtonWrapStyle}>
                    <AddToFavouritesButton
                        className={addToFavouritesButtonStyle}
                        propertyId={propertyId}
                        offerId={offerId}
                        theme={sidebarBg ? sidebarBg : "light"}
                        amplitudePlacement="rp_favourite_sidebar"
                    />
                </div>
            )}
        </>
    );
};

const cardWrapper = css({
    width: "100%",
    backgroundColor: "var(--colors-white)",
    height: "fit-content",
    borderRadius: 2,
    boxShadow: "var(--shadows-2)"
});

const contactWrapper = css({
    p: 3
});

const addToFavouritesButtonWrapStyle = css({
    display: "flex",
    justifyContent: "center",
    marginTop: 2
});

const addToFavouritesButtonStyle = css({
    //  We should place elements with margin-bottom, but since this element is dynamic, it needs to own the margin
    marginTop: 2
});
import {ReactNode} from "react";
import {css} from "@panda/css";
import cn from "classnames";

import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {OfferStatusStatus} from "@pg-rp/common-types/src/types/OfferStatusStatus";

import {AddToFavouritesButton} from "../../../favorite/components/AddToFavouritesButton";
import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../../hooks/use_should_show_price_by_offer_config_and_user";
import {PropertyFlatType} from "../../../offer/detail/constants/PropertyFlatType";
import {IOfferSalesOffice} from "../../../offer/types/IOfferSalesOffice";
import {OfferModalActiveTab} from "../../../offer/types/OfferModalActiveTab";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyListProperty} from "../../../property/types/PropertyListProperty";
import {AmplitudeAuthPlacement} from "../../../tracking/types/AmplitudeAuthPlacement";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {useLeadData} from "../../hooks/use_lead_data";
import {ApplicationFormVariant} from "../../types/form/ApplicationFormVariant";
import {GTMEventApplicationSourceSection} from "../../types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../utils/form/application_form_source";
import {getApplicationFormBoxCtaText} from "../../utils/lead/get_application_form_box_cta_text";
import {getApplicationLeadButtonText} from "../../utils/lead/get_application_lead_button_text";
import {ApplicationModalButtonWithHiddenPrices, IApplicationModalButtonWithHiddenPricesRenderProps} from "../ApplicationModalButtonWithHiddenPrices";
import {ApplicationTermsAcceptation} from "../ApplicationTermsAcceptation";
import {ApplicationFormBoxDefaultRender} from "./ApplicationFormBoxDefaultRender";
import {ApplicationFormBoxHeading} from "./ApplicationFormBoxHeading";
import {ApplicationFormBoxHiddenPriceVariant} from "./ApplicationFormBoxHiddenPriceVariant";
import {ApplicationFormBoxPhoneNumber} from "./ApplicationFormBoxPhoneNumber";

interface IProps {
    applicationVariant?: ApplicationFormVariant;
    customApplicationButtonText?: string;
    source: ApplicationSource;
    sourceSection: ApplicationSourceSection;
    optionalVendorInfoData?: {
        name: string;
        logo: {
            v_log_120x90: string;
        } | null;
    };
    optionalOfferPhoneData?: IOfferPhoneData;
    offerStatusStatus?: OfferStatusStatus;
    isAutoresponderEnabled: boolean;
    sidebarBg?: "light" | "dark";
    vendorId: number;
    offerId?: number;
    property?: (IPropertyDetail | IPropertyListProperty) & {flat_type?: PropertyFlatType};
    forceClose?: boolean;
    disableVendorInfo?: boolean;
    formButtonText?: string;
    render?: (props: IApplicationModalButtonWithHiddenPricesRenderProps) => ReactNode;
    className?: string;
    footer?: ReactNode;
    officeData?: IOfferSalesOffice;
    modalState?: OfferModalActiveTab | null;
}

interface IOfferPhoneData {
    configuration: {
        phone_clip: string | null;
    };
    is_phone_clip_enabled: boolean;
}

export const ApplicationFormBox = (props: IProps) => {
    const {
        customApplicationButtonText,
        source,
        sourceSection,
        optionalVendorInfoData,
        optionalOfferPhoneData,
        isAutoresponderEnabled,
        sidebarBg,
        vendorId,
        offerId,
        property,
        forceClose,
        className,
        modalState,
        offerStatusStatus
    } = props;

    const propertyId = property?.id;

    const {shouldHidePrices} = useShouldHidePrices();
    const leadData = useLeadData({
        vendorId,
        offerId,
        propertyId
    });

    const isAuthenticated = useIsAuthenticated();
    const isDataFetched = Boolean(leadData);

    const vendor = optionalVendorInfoData ?? leadData?.vendor?.detail;
    const isOfferStatusToAcquire = offerStatusStatus === OfferStatusStatus.STATUS_STATUS_TO_ACQUIRE;
    const propertyPrice = leadData?.property?.detail?.price;
    const vendorName = vendor?.name ?? vendorId.toString();
    const offerName = leadData?.offer?.detail?.name ?? (offerId ? offerId.toString() : "");

    const shouldShowPriceByConfig = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: leadData?.offer?.detail?.configuration.price_visibility,
        vendorPriceVisibility: leadData?.vendor?.detail?.configuration.price_visibility,
        vendorId
    });

    const {shouldShowPrice, ctaText} = getApplicationFormBoxCtaText({
        shouldHidePrices,
        isDataFetched,
        propertyId,
        propertyPrice
    });

    let defaultRenderSource;

    switch (modalState) {
        case OfferModalActiveTab.MAP:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_MAP;
            break;
        case OfferModalActiveTab.PLAN:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_FLOOR_PLAN;
            break;
        case OfferModalActiveTab.CONSTRUCTION_GALLERY:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_CONSTRUCTION_GALLERY;
            break;
        case OfferModalActiveTab.GALLERY:
        default:
            defaultRenderSource = GTMEventApplicationSourceSection.MODAL_GALLERY;
    }

    const amplitudePlacement: AmplitudeAuthPlacement = modalState ? "rp_developer_section_details" : "rp_developer_section_main";

    const gtmDefaultRenderData = {
        clickText: getApplicationLeadButtonText({
            isPriceHidden: !shouldShowPrice,
            isProperty: !!propertyId,
            propertyPrice: propertyPrice
        }),
        source: modalState ? defaultRenderSource : GTMEventApplicationSourceSection.SIDEBAR_LEAD,
        vendorName,
        offerName
    };

    const renderApplicationButton = (renderProps: IApplicationModalButtonWithHiddenPricesRenderProps) => {
        if (!shouldShowPrice) {
            const priceRevealButtonTestId = propertyId
                ? OFFER_TEST_IDS.PROPERTY_VIEW.REVEAL_PRICE_BUTTON
                : OFFER_TEST_IDS.OFFER_VIEW.SIDEBAR_BUTTON_SHOW_PRICES;

            return (
                <ApplicationFormBoxHiddenPriceVariant
                    {...renderProps}
                    buttonText={customApplicationButtonText}
                    priceRevealButtonTestId={priceRevealButtonTestId}
                />
            );
        }

        if (props.render) {
            return props.render(renderProps);
        }

        return (
            <ApplicationFormBoxDefaultRender
                vendorId={vendorId}
                isProperty={!!propertyId}
                price={propertyPrice}
                shouldShowPrice={shouldShowPrice}
                handleClick={renderProps.handleClick}
            />
        );
    };

    return (
        <>
            <div className={cn(cardWrapper, className)}>
                <div className={contactWrapper} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.SIDEBOX}>
                    <ApplicationFormBoxHeading
                        vendorName={vendor?.name}
                        vendorLogo={vendor?.logo && vendor.logo.v_log_120x90}
                        price={propertyPrice}
                        isDataFetched={isDataFetched}
                        shouldShowPrice={shouldShowPriceByConfig}
                        variant={shouldShowPriceByConfig ? "visible" : "blur"}
                        currency={leadData?.property?.detail?.currency}
                        offerType={property?.offer.type}
                        flatType={property?.flat_type}
                        isHolidayLocation={property?.offer.is_holiday_location}
                        isCondoHotel={property?.offer.is_condohotel}
                    />

                    <ApplicationModalButtonWithHiddenPrices
                        source={source}
                        sourceSection={sourceSection}
                        vendorId={vendorId}
                        offerId={offerId || null}
                        propertyId={propertyId || null}
                        forceClose={forceClose}
                        shouldShowPrice={shouldShowPrice}
                        gtmData={{...gtmDefaultRenderData, clickText: ctaText}}
                        amplitudePlacement={amplitudePlacement}
                        render={renderApplicationButton}
                        applicationVariant={props.applicationVariant}
                        currency={property?.offer.currency}
                    />

                    <ApplicationFormBoxPhoneNumber
                        isAutoresponderEnabled={isAutoresponderEnabled}
                        isDataFetched={isDataFetched}
                        offerId={offerId}
                        offer={leadData?.offer?.detail}
                        propertyId={propertyId}
                        optionalOfferPhoneData={optionalOfferPhoneData}
                        useHotlineNumber={isOfferStatusToAcquire}
                    />

                    <ApplicationTermsAcceptation className={css({mt: 2})} sourceForm={{source: "applicationSideBar", isUserLogged: isAuthenticated}} />

                    <Text align="center" color="var(--colors-gray-700)" variant="info_txt_1">
                        Administratorem Twoich danych
                        <br /> osobowych jest Property Group sp. z o.o
                    </Text>
                </div>

                {props.footer}
            </div>

            {propertyId && offerId && (
                <div className={addToFavouritesButtonWrapStyle}>
                    <AddToFavouritesButton
                        className={addToFavouritesButtonStyle}
                        propertyId={propertyId}
                        offerId={offerId}
                        theme={sidebarBg ? sidebarBg : "light"}
                        amplitudePlacement="rp_favourite_sidebar"
                    />
                </div>
            )}
        </>
    );
};

const cardWrapper = css({
    width: "100%",
    backgroundColor: "var(--colors-white)",
    height: "fit-content",
    borderRadius: 2,
    boxShadow: "var(--shadows-2)"
});

const contactWrapper = css({
    p: 3
});

const addToFavouritesButtonWrapStyle = css({
    display: "flex",
    justifyContent: "center",
    marginTop: 2
});

const addToFavouritesButtonStyle = css({
    //  We should place elements with margin-bottom, but since this element is dynamic, it needs to own the margin
    marginTop: 2
});
